import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Video, ExternalLink, RefreshCw } from "lucide-react";
import axios from "../../api/axios";
import toast from "react-hot-toast";

// Import central theme classes from MasterCssClass.js
import {
  masterPanelCard,
  masterHeadingMain,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterBtnBack,
  masterBadgeLive,
  masterVideoGrid,
  masterVideoCard,
  masterVideoFrameWrap,
  masterVideoOverlay,
  masterVideoPlayBtn,
  masterVideoInfoWrap,
  masterVideoTitle,
  masterVideoDesc,
  masterVideoFooter,
  masterVideoYoutubeBtn,
} from "../../GlobalComponents/MasterCssClass";

const VideoView = () => {
  const { examCode } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const targetCatId = queryParams.get("catId") || "";
  const targetCatName = queryParams.get("catName") || "";

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideoId, setActiveVideoId] = useState(null);

 const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/video-master/all").catch(() => null);
      if (res && res.data && res.data.success) {
        const list = res.data.data || [];
        
        // STRICT FILTERING: Match Exam Code AND (Category ID OR Category Name)
        const filtered = list.filter((v) => {
          const matchExam = String(v.examMasterId || "").toUpperCase() === String(examCode || "").toUpperCase();
          
          if (!matchExam) return false;

          if (targetCatId || targetCatName) {
            const matchCatId = targetCatId && String(v.examCategoryId || "").toUpperCase() === String(targetCatId).toUpperCase();
            const matchCatName = targetCatName && String(v.catName || "").toUpperCase() === String(targetCatName).toUpperCase();
            return matchCatId || matchCatName;
          }

          return true;
        });

        setVideos(filtered);
      } else {
        setVideos([]);
      }
    } catch {
      setVideos([]);
      toast.error("FAILED TO LOAD VIDEOS");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [examCode, targetCatId, targetCatName]);

  const getEmbedUrl = (url, isPlaying) => {
    try {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        return `https://www.youtube.com/embed/${match[2]}?autoplay=${isPlaying ? 1 : 0}&enablejsapi=1`;
      }
      return null;
    } catch {
      return null;
    }
  };

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar p-2 space-y-2`}>
      {/* HEADER CARD */}
      <div className={`${masterPanelCard} p-2`}>
        <div className={masterHeaderContent}>
          <div className={masterHeaderBrand}>
            <div className={masterHeaderIconBox}>
              <Video size={13} />
            </div>
            <h1 className={masterHeadingMain}>
              Video Portal  {targetCatName ? `- ${targetCatName}` : ""}
            </h1>
          </div>

          <div className={masterHeaderActions}>
            <button
              type="button"
              onClick={fetchVideos}
              className={masterBtnBack}
              title="Refresh Videos"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              <span>SYNC</span>
            </button>
          </div>
        </div>
      </div>

      {/* CONTENT GRID */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 font-bold text-xs">Loading video sessions...</div>
      ) : videos.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border text-slate-400 font-bold text-xs uppercase">
          No video sessions available for this category yet.
        </div>
      ) : (
        <div className={masterVideoGrid}>
          {videos.map((vid) => {
            const isPlaying = activeVideoId === vid._id;
            const embedUrl = getEmbedUrl(vid.youtubeUrl, isPlaying);

            return (
              <div key={vid._id} className={masterVideoCard}>
                <div>
                  <div className={masterVideoFrameWrap}>
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        title={vid.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs font-bold">
                        Invalid Link
                      </div>
                    )}

                    {!isPlaying && (
                      <div 
                        onClick={() => setActiveVideoId(vid._id)}
                        className={masterVideoOverlay}
                      >
                        <div className={masterVideoPlayBtn}>
                          ▶
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={masterVideoInfoWrap}>
                    <h2 className={masterVideoTitle} title={vid.title}>
                      {vid.title}
                    </h2>
                    {vid.catName && (
                      <span className="inline-block text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded uppercase">
                        {vid.catName}
                      </span>
                    )}
                    {vid.description && (
                      <p className={masterVideoDesc}>{vid.description}</p>
                    )}
                  </div>
                </div>

                {/* <div className={masterVideoFooter}>
                  <span className={masterBadgeLive}>
                    {vid.status || "ACTIVE"}
                  </span>
                  <a
                    href={vid.youtubeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={masterVideoYoutubeBtn}
                  >
                    <ExternalLink size={11} /> YouTube
                  </a>
                </div> */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VideoView;